import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';
import { DOCUMENT_FILE_FIELDS, toDocumentList } from '../../utils/residenceDocuments';

/**
 * Manager Residences Service
 * Routes dynamically based on role_id from localStorage user.role.id
 */

/**
 * Build the request body for the residence create/convert/update endpoints.
 *
 * Every document slot is an array that may mix already-stored paths with
 * `File`s the manager just picked. A File cannot travel as JSON
 * (JSON.stringify turns it into `{}`, which the API rejects with
 * "photo_id_url cannot be an array or an object"), so when any slot holds one
 * the whole payload goes as multipart:
 *
 *   - each File → its own part named after the column (repeated names are
 *     collected into an array by multer)
 *   - the JSON part keeps only the stored paths, which tells the API which
 *     existing files to KEEP — anything left out is deleted server-side
 *
 * With no Files anywhere the plain JSON object is sent unchanged (the path
 * arrays alone are enough to express a removal).
 *
 * @param {object} payload  { personalDetails, ..., documentations }
 * @returns {{body: FormData|object, config: object}}
 */
const buildResidencePayload = (payload = {}) => {
  const { documentations, ...rest } = payload;

  // Callers that don't touch documents (medication, emergency notes...) pass
  // straight through, so a partial update never mentions the document slots.
  if (!documentations) return { body: payload, config: {} };

  const docs = documentations;

  // Split each slot into { keep: [paths], upload: [Files] }.
  const slots = {};
  let fileCount = 0;
  DOCUMENT_FILE_FIELDS.forEach((field) => {
    if (docs[field] === undefined) return;
    const list = toDocumentList(docs[field]);
    const upload = list.filter((item) => item instanceof File);
    slots[field] = { keep: list.filter((item) => typeof item === 'string'), upload };
    fileCount += upload.length;
  });

  // Normalised documentations for the JSON part: stored paths only.
  const docsJson = { ...docs };
  Object.entries(slots).forEach(([field, { keep }]) => { docsJson[field] = keep; });

  // Named additional documents. They all travel in the one multipart field the
  // API already accepts (`additional_id_document_url`) because multer's field
  // list is fixed, so each row declares how many of those files are its own and
  // the API hands them out in the same order they are appended below.
  let additionalUploads = [];
  if (Array.isArray(docs.additional_documents)) {
    docsJson.additional_documents = docs.additional_documents.map((row) => {
      const list = toDocumentList(row?.files);
      const upload = list.filter((item) => item instanceof File);
      additionalUploads = [...additionalUploads, ...upload];
      return {
        name: row?.name || '',
        files: list.filter((item) => typeof item === 'string'),
        new_files: upload.length,
      };
    });
    fileCount += additionalUploads.length;
  }

  if (fileCount === 0) {
    return { body: { ...rest, documentations: docsJson }, config: {} };
  }

  const formData = new FormData();
  Object.entries(slots).forEach(([field, { upload }]) => {
    upload.forEach((file) => formData.append(field, file));
  });
  // Appended in row order — that order IS the mapping back to each named row.
  additionalUploads.forEach((file) => formData.append('additional_id_document_url', file));
  formData.append('data', JSON.stringify({ ...rest, documentations: docsJson }));

  return {
    body: formData,
    config: { headers: { 'Content-Type': 'multipart/form-data' } },
  };
};

const residencesService = {
  /**
   * Get all residences with optional search
   */
  getAllResidences: async (params = {}) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching residences:', error);
      throw error;
    }
  },

  /**
   * Get residence by ID
   */
  getResidenceById: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching residence:', error);
      throw error;
    }
  },

  /**
   * Create new residence
   */
  createResidence: async (residenceData) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.post(`/${routePath}/residences`, residenceData);
      return response.data;
    } catch (error) {
      console.error('Error creating residence:', error);
      throw error;
    }
  },

  /**
   * Update residence
   */
  updateResidence: async (residenceId, residenceData) => {
    try {
      const routePath = getManagerRoutePath();
      const { body, config } = buildResidencePayload(residenceData);
      const response = await axiosInstance.put(`/${routePath}/residences/${residenceId}`, body, config);
      return response.data;
    } catch (error) {
      console.error('Error updating residence:', error);
      throw error;
    }
  },

  /**
   * Upload / replace the client's profile picture (multipart).
   */
  uploadProfilePic: async (residenceId, file) => {
    try {
      const routePath = getManagerRoutePath();
      const fd = new FormData();
      fd.append('profile_pic', file);
      const response = await axiosInstance.post(`/${routePath}/residences/${residenceId}/profile-pic`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  },

  /**
   * Delete residence
   */
  deleteResidence: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.delete(`/${routePath}/residences/${residenceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting residence:', error);
      throw error;
    }
  },

  /**
   * Get medication administration history (per-dose MAR log from the app)
   */
  getMedicationAdministrations: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/medication-administrations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching medication administrations:', error);
      throw error;
    }
  },

  /**
   * Get residence care notes.
   *
   * `residenceId` may be 'all' for every resident in the organisation.
   * `params` (all optional): { from, to, handover, category } — see the
   * controller. No params = the resident's full history.
   */
  getResidenceCareNotes: async (residenceId, params = {}) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/care-notes`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching residence care notes:', error);
      throw error;
    }
  },

  /**
   * Get residence nutrient data (Food & Fluid)
   */
  getNutrientData: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/nutrients`);
      return response.data;
    } catch (error) {
      console.error('Error fetching nutrient data:', error);
      throw error;
    }
  },

  /**
   * Get the resident's bladder and bowel record, built from the toileting
   * actions logged in their care notes.
   */
  getContinenceData: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/continence`);
      return response.data;
    } catch (error) {
      console.error('Error fetching continence data:', error);
      throw error;
    }
  },

  /**
   * Set a resident's daily calorie / fluid targets. Writes the same values the
   * mobile app writes, so both measure intake against one target.
   */
  updateNutritionGoals: async (residenceId, { calories, fluidMl } = {}) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.put(
        `/${routePath}/residences/${residenceId}/nutrition/goals`,
        {
          ...(calories !== undefined ? { calories } : {}),
          ...(fluidMl !== undefined ? { fluidMl } : {}),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating nutrition goals:', error);
      throw error;
    }
  },

  /**
   * Get a resident's aggregated emergency info (same data the mobile app shows).
   */
  getEmergencyInfo: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/emergency-info`);
      return response.data;
    } catch (error) {
      console.error('Error fetching emergency info:', error);
      throw error;
    }
  },

  /**
   * Get residence incidents
   */
  getResidentIncidents: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/incidents`);
      return response.data;
    } catch (error) {
      console.error('Error fetching incidents:', error);
      throw error;
    }
  },

  /**
   * Create a new incident for a residence
   */
  createIncident: async (residenceId, incidentData) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.post(`/${routePath}/residences/${residenceId}/incidents`, incidentData);
      return response.data;
    } catch (error) {
      console.error('Error creating incident:', error);
      throw error;
    }
  },

  /**
   * Update an existing incident (used by the Body Map tab to edit markers,
   * set injury status, add or remove marks).
   */
  updateIncident: async (residenceId, incidentId, incidentData) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.put(`/${routePath}/residences/${residenceId}/incidents/${incidentId}`, incidentData);
      return response.data;
    } catch (error) {
      console.error('Error updating incident:', error);
      throw error;
    }
  },

  /**
   * Get appointments for a residence
   */
  getResidentAppointments: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/appointments`);
      return response.data;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  },

  /**
   * Create a new appointment for a residence
   */
  createAppointment: async (residenceId, appointmentData) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.post(`/${routePath}/residences/${residenceId}/appointments`, appointmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },

  /**
   * Delete an appointment
   */
  deleteAppointment: async (residenceId, appointmentId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.delete(`/${routePath}/residences/${residenceId}/appointments/${appointmentId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  },

  /**
   * Get residence behaviour data
   */
  getBehaviourData: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/behaviour`);
      return response.data;
    } catch (error) {
      console.error('Error fetching behaviour data:', error);
      throw error;
    }
  },

  /**
   * Get residence vital signs
   */
  getResidentVitals: async (residenceId, type) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/vitals`, {
        params: { type }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching vital signs:', error);
      throw error;
    }
  },

  /**
   * Get all enquiries
   */
  getAllEnquiries: async (params = {}) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/enquiries`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      throw error;
    }
  },

  /**
   * Create new enquiry
   */
  createEnquiry: async (enquiryData) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.post(`/${routePath}/residences/enquiries`, enquiryData);
      return response.data;
    } catch (error) {
      console.error('Error creating enquiry:', error);
      throw error;
    }
  },

  /**
   * Create a client / resident from the full 6-step form, with no enquiry
   * behind it. Same payload as convertEnquiryToClient — the API makes the
   * master row and its six section rows in one go.
   */
  createClientDirect: async (clientData) => {
    try {
      const routePath = getManagerRoutePath();
      const { body, config } = buildResidencePayload(clientData);
      const response = await axiosInstance.post(`/${routePath}/residences/full`, body, config);
      return response.data;
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  },

  /**
   * Convert Enquiry to Client
   */
  convertEnquiryToClient: async (residenceId, conversionData) => {
    try {
      const routePath = getManagerRoutePath();
      const { body, config } = buildResidencePayload(conversionData);
      const response = await axiosInstance.post(`/${routePath}/residences/${residenceId}/convert`, body, config);
      return response.data;
    } catch (error) {
      console.error('Error converting client:', error);
      throw error;
    }
  },
};

export default residencesService;
